import type { FC, ReactNode } from 'react'
import type { Wallpaper } from '@/types'

import Link from 'next/link'
import Image from 'next/image'
import { ArrowDownToLine } from 'lucide-react'
import { cn } from '@/lib/utils'
import { toCssColor } from '@/lib/colors'
import { getAspectRatio } from '@/lib/format'

interface WallpaperCardProps {
    wallpaper: Wallpaper
    sizes?: string
    aspect?: 'natural' | 'phone'
    preload?: boolean
    className?: string
}

export const WallpaperCard: FC<WallpaperCardProps> = ({
    wallpaper,
    sizes = '(min-width: 1280px) 25vw, (min-width: 768px) 33vw, 50vw',
    aspect = 'natural',
    preload = false,
    className
}): ReactNode => {
    const ratio = aspect === 'phone' ? 9 / 16 : getAspectRatio(wallpaper)
    const hasSize = Boolean(wallpaper.width && wallpaper.height)
    const hasCategory = Boolean(wallpaper.category_id && wallpaper.category_name)

    return (
        <article
            className={cn('group relative overflow-hidden rounded-2xl ring-1 ring-white/[0.06]', className)}
            style={{ backgroundColor: toCssColor(wallpaper.color) }}
        >
            <Link
                href={`/wallpapers/${wallpaper.slug}`}
                className='block rounded-2xl focus-visible:ring-3 focus-visible:ring-ring/70 focus-visible:outline-none focus-visible:ring-inset'
            >
                <div className='relative w-full' style={{ aspectRatio: ratio }}>
                    <Image
                        src={wallpaper.image}
                        alt={wallpaper.image_alt || wallpaper.title}
                        fill
                        sizes={sizes}
                        preload={preload}
                        className='object-cover transition-transform duration-700 ease-out group-hover:scale-[1.04] motion-reduce:transition-none motion-reduce:group-hover:scale-100'
                    />
                </div>

                <div className='absolute inset-x-0 bottom-0 flex flex-col gap-1 bg-linear-to-t from-black/80 via-black/35 to-transparent p-3 pt-12 transition-opacity duration-300 sm:p-4 sm:pt-14 md:opacity-0 md:group-focus-within:opacity-100 md:group-hover:opacity-100'>
                    <h3
                        className='truncate text-sm font-medium text-white sm:text-[0.95rem]'
                        data-mdlfy-cms={`wallpapers:${wallpaper.id}:title`}
                    >
                        {wallpaper.title}
                    </h3>

                    {(hasCategory || hasSize) && (
                        <p className='hidden items-center gap-1.5 text-xs text-white/70 sm:flex'>
                            {hasCategory && (
                                <span data-mdlfy-cms={`categories:${wallpaper.category_id}:name`}>
                                    {wallpaper.category_name}
                                </span>
                            )}

                            {hasCategory && hasSize && (
                                <span aria-hidden='true'>
                                    ·
                                </span>
                            )}

                            {hasSize && (
                                <span className='font-mono text-[0.7rem]'>
                                    <span>{wallpaper.width}</span>
                                    {' × '}
                                    <span>{wallpaper.height}</span>
                                </span>
                            )}
                        </p>
                    )}
                </div>
            </Link>

            <a
                href={`/api/download/${wallpaper.slug}`}
                rel='nofollow'
                aria-label={`Download ${wallpaper.title}`}
                className='absolute top-3 right-3 hidden size-9 items-center justify-center rounded-xl bg-black/45 text-white opacity-0 backdrop-blur-md transition hover:bg-black/70 focus-visible:opacity-100 focus-visible:ring-3 focus-visible:ring-ring/70 focus-visible:outline-none group-hover:opacity-100 md:inline-flex'
            >
                <ArrowDownToLine aria-hidden='true' className='size-4' />
            </a>
        </article>
    )
}
